import React, { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
    Paper, List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction, Divider,
    Switch, TextField, InputAdornment, Chip, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, CircularProgress, IconButton
} from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined';
import InfoIcon from '@material-ui/icons/Info';
import RefreshIcon from '@material-ui/icons/Refresh';
import { MyProfileNav } from '../../elements';
import { PageLoader } from '../../shared_elements';
import { globalGetService, globalPostService } from '../../../globalServices';
import { checkApiStatus, getLocalStorageInfo } from '../../utils_v2';
import { regexConstants } from '../../../constants/regEx';


const OTP_VALIDITY_SECONDS = 10 * 60;

const PasswordManagement = () => {
    const { enqueueSnackbar } = useSnackbar();
    const isAdmin = getLocalStorageInfo()?.user?.group_profile_order?.id === 0;
    const [isLoading, setLoading] = useState(true);
    const [settings, setSettings] = useState({ enabled: false, expiry_days: 90, updated_by: null, updated_at: null });
    const [expiryInput, setExpiryInput] = useState('');
    const [expiryError, setExpiryError] = useState('');
    const [otpState, setOtpState] = useState({ open: false, action: '', otp: '', error: '', sending: false, verifying: false, secondsLeft: 0, });
    const timerRef = useRef(null);
   
    useEffect(() => {
        if (isAdmin) {
            getSettings();
        } else {
            setLoading(false);
        }
        return () => clearInterval(timerRef.current);
    }, []);

    const getSettings = () => {
        setLoading(true);
        globalGetService('api/security/password-policy/')
            .then(response => {
                if (checkApiStatus(response)) {
                    setSettings(response?.data?.data);
                    setExpiryInput(String(response.data?.data?.expiry_days ?? ''));
                    setLoading(false);
                }else{
                     setLoading(false);
                }
            })
    }

    const startOtpTimer = () => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setOtpState(prev => {
                if (prev.secondsLeft <= 1) {
                    clearInterval(timerRef.current);
                    return { ...prev, secondsLeft: 0 };
                }
                return { ...prev, secondsLeft: prev.secondsLeft - 1 };
            });
        }, 1000);
    }

    const requestOtp = (action) => {
        setOtpState(prev => ({ ...prev, open: true, action, otp: '', error: '', sending: true }));
        globalPostService('api/security/password-policy/request-otp/', { "enabled": action === 'enable', "expiry_days": expiryInput || 90 })
            .then(response => {
                if (checkApiStatus(response)) {
                    enqueueSnackbar(response.data.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                    setOtpState(prev => ({ ...prev, sending: false, secondsLeft: OTP_VALIDITY_SECONDS, otp: '', }));
                    startOtpTimer();
                }
            })
    }

    const closeOtpDialog = () => {
        clearInterval(timerRef.current);
        setOtpState({ open: false, action: '', otp: '', error: '', sending: false, verifying: false, secondsLeft: 0 });
    }

    const verifyOtpAndApplyToggle = () => {
        if (!otpState.otp?.trim().length || otpState.otp.trim().length !== 6) {
            setOtpState(prev => ({ ...prev, error: 'Please enter the 6 digit OTP' }));
            return;
        }
        if (otpState.secondsLeft <= 0) {
            setOtpState(prev => ({ ...prev, error: 'This OTP has expired. Please request a new one.' }));
            return;
        }
        setOtpState(prev => ({ ...prev, verifying: true, error: '' }));
        globalPostService('api/security/password-policy/update/', { otp: otpState.otp, })
            .then(response => {
                if (checkApiStatus(response)) {
                    const enabled = otpState.action === 'enable';
                    setSettings(prev => ({ ...prev, enabled, updated_by: getLocalStorageInfo()?.user?.name || 'You', updated_at: new Date().toISOString() }));
                    enqueueSnackbar(`Password Management Validations ${enabled ? 'enabled' : 'disabled'} successfully`, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                    closeOtpDialog();
                    getSettings();
                } else {
                    setOtpState(prev => ({ ...prev, verifying: false, error: 'Incorrect OTP. Please try again.' }));
                }
            })
    }

    const onToggleChange = (checked) => {
        if (checked) {
            if (!expiryInput?.trim().length) {
                setExpiryError('Please enter expiry period');
                return;
            }
            const expiryDays = Number(expiryInput);
            if (expiryDays < 1 || expiryDays > 365) {
                setExpiryError('Expiry period must be between 1 and 365 days.');
                return;
            }
        }
        requestOtp(checked ? 'enable' : 'disable');
    }

    const onExpiryChange = (value) => {
        if (value === '' || regexConstants.onlyNumeric.test(value)) {
            setExpiryInput(value);
            setExpiryError('');
        }
    }

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    }

    if (!isAdmin) {
        return (
            <section className='roles-management' style={{ margin: '24px 0 0 276px' }}>
                <MyProfileNav />
                <Paper style={{ padding: '40px', textAlign: 'center' }}>
                    Sorry, you do not have access to Password Management settings.<br />
                    Please contact your SPARTA Admin for access.
                </Paper>
            </section>
        )
    }

    return (
        <section className='roles-management' style={{ margin: '24px 0 0 276px' }}>
            <MyProfileNav />
            <Paper style={{ position: 'relative', minHeight: '200px' }}>
                <List>
                    <ListItem>
                        <ListItemIcon><LockOutlinedIcon color={settings.enabled ? 'primary' : 'disabled'} /></ListItemIcon>
                        <ListItemText
                            primary={
                                <span>
                                    Password Management
                                    <Tooltip title="Enables or disables all password security controls (periodic rotation, reuse prevention and account recovery restriction) across the platform" arrow>
                                        <InfoIcon fontSize='small' style={{ verticalAlign: 'middle', marginLeft: '5px', color: '#9e9e9e' }} />
                                    </Tooltip>
                                </span>
                            }
                            secondary="Enforce password security controls platform-wide"
                        />
                        <ListItemSecondaryAction style={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                                size='small'
                                label={settings.enabled ? 'Enabled' : 'Disabled'}
                                style={{ marginRight: '12px', backgroundColor: settings.enabled ? '#e8f5e9' : '#f5f5f5', color: settings.enabled ? '#2e7d32' : '#757575' }}
                            />
                            <Switch
                                checked={settings.enabled}
                                onChange={(e) => onToggleChange(e.target.checked)}
                                color="primary"
                                name="password_management_toggle"
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                        <ListItemIcon><ScheduleOutlinedIcon color={settings.enabled ? 'primary' : 'disabled'} /></ListItemIcon>
                        <ListItemText primary="Password expiry period" secondary="Number of days before a password must be changed (max 365)" />
                        <ListItemSecondaryAction>
                            <span style={{ color: 'red', marginRight: '10px' }}>{expiryError}</span>
                            <TextField
                                value={expiryInput}
                                onChange={(e) => onExpiryChange(e.target.value)}
                                placeholder='e.g. 30'
                                margin='none'
                                variant='outlined'
                                style={{ width: '140px' }}
                                disabled={settings.enabled}
                                inputProps={{ maxLength: 3, style: { textAlign: 'right', height: '40px', padding: '0 14px' } }}
                                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                        <ListItemIcon><MailOutlineIcon color={settings.enabled ? 'primary' : 'disabled'} /></ListItemIcon>
                        <ListItemText primary="Expiry reminder emails" secondary="Sent to active users only" />
                        <ListItemSecondaryAction>
                            <Chip size='small' label='7 days before' style={{ marginRight: '8px' }} variant='outlined' />
                            <Chip size='small' label='1 day before' variant='outlined' />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                        <ListItemIcon><VerifiedUserOutlinedIcon color={settings.enabled ? 'primary' : 'disabled'} /></ListItemIcon>
                        <ListItemText primary="Password reuse prevention" secondary="Last 5 passwords cannot be reused" />
                        <ListItemSecondaryAction>
                            <span style={{ fontStyle: 'italic', color: settings.enabled ? '#2e7d32' : '#9e9e9e', fontSize: '13px' }}>
                                Always on when enabled
                            </span>
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                        <ListItemIcon><SecurityOutlinedIcon color={settings.enabled ? 'primary' : 'disabled'} /></ListItemIcon>
                        <ListItemText primary="Account recovery restriction" secondary="Old passwords and PINs blocked on recovery" />
                        <ListItemSecondaryAction>
                            <span style={{ fontStyle: 'italic', color: settings.enabled ? '#2e7d32' : '#9e9e9e', fontSize: '13px' }}>
                                Always on when enabled
                            </span>
                        </ListItemSecondaryAction>
                    </ListItem>
                </List>
                {settings.updated_by ?
                    <div style={{ padding: '10px 16px', fontSize: '12px', color: '#9e9e9e' }}>
                        Last updated by {settings.updated_by} on {new Date(settings.updated_at).toLocaleString()}
                    </div> : null
                }
                {isLoading ? <PageLoader /> : null}
            </Paper>

            <Dialog open={otpState.open} maxWidth='xs' fullWidth>
                <DialogTitle >Confirm {otpState.action === 'enable' ? 'Enable' : 'Disable'} Password Management</DialogTitle>
                <DialogContent dividers>
                    <p style={{ fontSize: '14px', color: '#616161', marginBottom: '20px' }}>
                        We've sent a One Time Password to your registered email address to confirm this change. This will {otpState.action === 'enable' ? 'enable' : 'disable'} all password management validations for every user on SPARTA.
                    </p>
                    <TextField
                        autoFocus
                        fullWidth
                        label='Enter OTP'
                        variant='outlined'
                        value={otpState.otp}
                        onChange={(e) => {
                            const value = e?.target?.value ?? '';
                            if (value === '' || regexConstants.onlyNumeric.test(value)) {
                                setOtpState(prev => ({ ...prev, otp: value, error: '' }));
                            }
                        }}
                        error={!!otpState.error}
                        helperText={otpState.error || ''}
                        inputProps={{ maxLength: 6, style: { height: '45px', padding: '0 14px' } }}
                        InputLabelProps={{ shrink: true }}
                        disabled={otpState.sending || otpState.verifying}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                        <span style={{ fontSize: '13px', color: otpState.secondsLeft > 0 ? '#616161' : '#f44336' }}>
                            {otpState.secondsLeft > 0 ? `OTP expires in ${formatTimer(otpState.secondsLeft)}` : ''}
                        </span>
                        <Button
                            size='small'
                            color='primary'
                            disabled={otpState?.secondsLeft > 0 || otpState?.sending}
                            onClick={() => requestOtp(otpState.action)}
                            startIcon={<RefreshIcon fontSize='small' />}
                        >
                            Resend OTP
                        </Button>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeOtpDialog}
                        disabled={otpState.verifying}>Cancel</Button>
                    <Button variant='contained' color='primary' onClick={verifyOtpAndApplyToggle}
                        disabled={otpState.sending || otpState.verifying}
                    >
                        {otpState.verifying ? <CircularProgress size={20} /> : 'Verify & Continue'}
                    </Button>
                </DialogActions>
            </Dialog>
        </section>
    )
}

export default PasswordManagement;
